import axios from 'axios';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { INowFormValues } from '@/types/now';
import type { IApiEnvelope } from '@/types/api';

const POSTAL_CODE_LOOKUP_ENDPOINT = '/api/register/postal-code-lookup';

interface IPostalCodeLookupResults {
    found: boolean;
    city: string;
    state: number | null;
}

/**
 * Fills City and State from the entered zip, the way the Razor page did on focusout.
 * A failed lookup leaves both fields alone so the donor can still type them by hand.
 */
export const usePostalCodeLookup = () => {
    const { getValues, setValue } = useFormContext<INowFormValues>();

    return useCallback(async () => {
        const code = getValues('postalCode').trim();

        if (!/^[0-9]{5}$/.test(code)) {
            return;
        }

        try {
            const response = await axios.get<IApiEnvelope<IPostalCodeLookupResults>>(
                `${POSTAL_CODE_LOOKUP_ENDPOINT}/${code}`,
                { headers: { Accept: 'application/json' } },
            );

            const results = response.data.results;

            if (!results?.found) {
                return;
            }

            setValue('city', results.city, { shouldValidate: true });
            setValue('state', results.state != null ? String(results.state) : '', { shouldValidate: true });
        } catch {
            // Lookup is a convenience; the donor can still fill both fields in themselves.
        }
    }, [getValues, setValue]);
};
